/**
 * Single entry point for starting consent-gated tracking.
 *
 * @overview
 * Starts PostHog and the Meta Pixel together, once, behind one consent check,
 * and re-runs on a later grant so a visitor who accepts mid-session begins
 * being measured without a reload.
 *
 * ## Behavioural change this introduced
 *
 * PostHog previously initialised unconditionally in `main.tsx`. It is now gated
 * on explicit consent, so funnel volumes show a step change on the day this
 * shipped — that discontinuity is the consent gate working, not a conversion
 * collapse. Annotate it in PostHog when it deploys, or a future investigation
 * will chase a drop that never happened.
 */

import { initPostHog } from './posthog';
import { startMetaPixelConsentBridge } from './metaPixel';
import { hasAnalyticsConsent, onConsentChange } from './consent';
import { captureAttribution } from './attributionCapture';

/**
 * Start everything that depends on consent, plus attribution capture.
 *
 * Attribution capture is deliberately NOT consent-gated: it records which of
 * our own channels sent a visitor, is first-party, contains no PII, and is used
 * to attribute revenue we already earned rather than to track anyone across
 * sites. Blocking it would leave paid orders permanently unattributed and make
 * the D2C margin comparison unmeasurable.
 */
export function startTracking(): void {
  captureAttribution(window.location.search, document.referrer);

  if (hasAnalyticsConsent()) {
    initPostHog();
  } else {
    const unsubscribe = onConsentChange((state) => {
      if (state === 'granted') {
        initPostHog();
        unsubscribe();
      }
    });
  }

  // Owns its own consent subscription and no-ops outside production.
  startMetaPixelConsentBridge();
}
